import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Badge } from "../../components/ui";
import {
  ArrowLeft,
  Shield,
  CheckCircle2,
  X,
  Phone,
  Wrench,
  DollarSign,
  Info,
} from "lucide-react";
import { cn } from "../../lib/utils";
import type { NewModel } from "./BrowseModels";
import { getAssetById, Asset } from "../../services/asset.api";

function formatCurrency(n: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

interface LocationState {
  model?: NewModel;
  tradeInValue?: number;
}

interface ProtectionPlan {
  id: string;
  name: string;
  provider: string;
  duration: number;
  price: number;
  monthlyPrice: number;
  tier: "basic" | "standard" | "premium";
  coverage: string[];
  notCovered?: string[];
  deductible: number;
  benefits: string[];
  tag?: string;
}

const PROTECTION_PLANS: ProtectionPlan[] = [
  {
    id: "basic-1yr",
    name: "Essential Protection",
    provider: "HomeGuard",
    duration: 1,
    price: 79,
    monthlyPrice: 7,
    tier: "basic",
    deductible: 75,
    tag: "Most Affordable",
    coverage: [
      "Mechanical breakdowns",
      "Electrical failures",
      "Parts & labor included",
    ],
    notCovered: [
      "Cosmetic damage",
      "User damage",
    ],
    benefits: [
      "Email support",
      "30-day claim processing",
      "Authorized service centers",
    ],
  },
  {
    id: "standard-2yr",
    name: "Complete Protection",
    provider: "Asurion",
    duration: 2,
    price: 149,
    monthlyPrice: 6,
    tier: "standard",
    deductible: 50,
    tag: "Best Value",
    coverage: [
      "Mechanical & electrical failures",
      "Normal wear and tear",
      "Unlimited claims",
      "Parts, labor & shipping",
    ],
    notCovered: [
      "Intentional damage",
      "Pre-existing conditions",
    ],
    benefits: [
      "24/7 phone support",
      "2-day claim processing",
      "Cash payout option available",
      "Easy online claims",
    ],
  },
  {
    id: "premium-3yr",
    name: "Ultimate Care",
    provider: "SquareTrade",
    duration: 3,
    price: 229,
    monthlyPrice: 6,
    tier: "premium",
    deductible: 0,
    tag: "Most Comprehensive",
    coverage: [
      "All mechanical & electrical issues",
      "Accidental damage (drops, spills)",
      "Power surge protection",
      "Unlimited repairs or replacement",
      "24-month manufacturer extension",
    ],
    benefits: [
      "24/7 priority support",
      "Same-day claim processing",
      "Free preventive maintenance check",
      "Transferable to new owner",
      "90-day money back guarantee",
    ],
  },
  {
    id: "premium-5yr",
    name: "Lifetime Care Plus",
    provider: "301io Premium",
    duration: 5,
    price: 349,
    monthlyPrice: 6,
    tier: "premium",
    deductible: 0,
    tag: "Ultimate Peace of Mind",
    coverage: [
      "Comprehensive coverage for all failures",
      "Accidental damage from handling",
      "Food loss reimbursement (refrigerators)",
      "Replacement guarantee",
      "Annual maintenance included",
    ],
    benefits: [
      "Dedicated concierge support",
      "Instant approval for most claims",
      "In-home diagnostics",
      "Loaner appliance during repairs",
      "Price-lock guarantee",
      "Automatic renewal discount",
    ],
  },
];

export function ProtectionPlans() {
  const { t } = useTranslation('upgrade');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const state = location.state as LocationState;

  const model = state?.model;
  const tradeInValue = state?.tradeInValue ?? 0;
  
  const [asset, setAsset] = useState<Asset | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string | null>("standard-2yr");
  const [showComparison, setShowComparison] = useState(false);

  // Load asset info from URL param (for warranty expired ads)
  useEffect(() => {
    const assetId = searchParams.get('assetId') || id;
    if (!assetId) return;
    getAssetById(assetId)
      .then((data) => setAsset(data))
      .catch((err) => console.error('Failed to load asset:', err));
  }, [id, searchParams]);

  if (!model) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-sm text-muted-foreground">{t('plans.noModelSelected')}</p>
        <Button variant="outline" onClick={() => navigate(`/assets/${id}/upgrade/browse`)}>
          {t('plans.backToBrowse')}
        </Button>
      </div>
    );
  }

  const handleContinue = () => {
    const plan = PROTECTION_PLANS.find((p) => p.id === selectedPlan);
    navigate(`/assets/${id}/upgrade/checkout`, {
      state: { model, tradeInValue, protectionPlan: plan },
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur shadow-md">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="h-9 w-9 rounded-full border flex items-center justify-center flex-shrink-0 hover:bg-secondary transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold">
              {asset ? `Protection Plans for ${asset.name}` : t('plans.title')}
            </h1>
            <p className="text-xs text-muted-foreground">
              {t('plans.subtitle')}
            </p>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Product info banner */}
        <div className="mb-6 rounded-xl border-2 bg-gradient-to-r from-blue-50 to-violet-50 dark:from-blue-950/20 dark:to-violet-950/20 p-6">
          <div className="flex items-start gap-4">
            <Shield className="h-8 w-8 text-blue-600 flex-shrink-0" />
            <div className="flex-1">
              <h2 className="text-xl font-bold mb-2">
                {model.brand} {model.name}
              </h2>
              <p className="text-sm text-muted-foreground mb-3">
                {t('plans.manufacturerWarrantyIncluded', { number: model.modelNumber, years: model.warrantyYears })}
              </p>
              <p className="text-sm leading-relaxed">
                {t('plans.extendCoverage')}
              </p>
            </div>
          </div>
        </div>

        {/* Toggle for comparison view */}
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t('plans.chooseLevel')}</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowComparison(!showComparison)}
          >
            {showComparison ? t('plans.hideComparison') : t('plans.showComparison')}
          </Button>
        </div>

        {/* Comparison table */}
        {showComparison && (
          <div className="mb-8 rounded-xl border-2 bg-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50">
                  <tr>
                    <th className="p-4 text-left font-semibold min-w-[150px]">{t('plans.feature')}</th>
                    {PROTECTION_PLANS.map((plan) => (
                      <th key={plan.id} className="p-4 text-center font-semibold min-w-[120px]">
                        {plan.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="p-4 font-medium">{t('plans.price')}</td>
                    {PROTECTION_PLANS.map((plan) => (
                      <td key={plan.id} className="p-4 text-center font-bold">
                        {formatCurrency(plan.price)}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="p-4 font-medium">{t('plans.duration')}</td>
                    {PROTECTION_PLANS.map((plan) => (
                      <td key={plan.id} className="p-4 text-center">
                        {plan.duration} {plan.duration === 1 ? t('plans.year') : t('plans.years')}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="p-4 font-medium">{t('plans.deductible')}</td>
                    {PROTECTION_PLANS.map((plan) => (
                      <td key={plan.id} className="p-4 text-center">
                        {plan.deductible === 0 ? t('plans.none') : formatCurrency(plan.deductible)}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="p-4 font-medium">{t('plans.support247')}</td>
                    {PROTECTION_PLANS.map((plan) => (
                      <td key={plan.id} className="p-4 text-center">
                        {plan.tier !== "basic" ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600 mx-auto" />
                        ) : (
                          <X className="h-5 w-5 text-muted-foreground mx-auto" />
                        )}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="p-4 font-medium">{t('plans.accidentalDamage')}</td>
                    {PROTECTION_PLANS.map((plan) => (
                      <td key={plan.id} className="p-4 text-center">
                        {plan.tier === "premium" ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600 mx-auto" />
                        ) : (
                          <X className="h-5 w-5 text-muted-foreground mx-auto" />
                        )}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Plans grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {PROTECTION_PLANS.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              selected={selectedPlan === plan.id}
              onSelect={() => setSelectedPlan(plan.id)}
            />
          ))}
        </div>

        {/* No protection option */}
        <div className="mb-8">
          <button
            onClick={() => setSelectedPlan(null)}
            className={cn(
              "w-full rounded-xl border-2 p-6 text-left transition-all",
              selectedPlan === null
                ? "border-foreground bg-secondary/50"
                : "border-border hover:border-foreground/30"
            )}
          >
            <div className="flex items-start gap-4">
              <X className="h-6 w-6 flex-shrink-0 text-muted-foreground mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold mb-1">{t('plans.skipProtection')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('plans.continueManufacturerOnly', { years: model.warrantyYears })}
                </p>
              </div>
              {selectedPlan === null && (
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
              )}
            </div>
          </button>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            variant="outline"
            size="lg"
            className="flex-1"
            onClick={() => navigate(`/assets/${id}/upgrade/model/${model.id}`)}
          >
            {t('plans.backToModelDetails')}
          </Button>
          <Button size="lg" className="flex-1" onClick={handleContinue}>
            {selectedPlan ? t('plans.continueWithPlan') : t('plans.continueWithoutProtection')}
          </Button>
        </div>

        {/* Why protection */}
        <div className="mt-8 rounded-xl border bg-card p-6">
          <h3 className="text-base font-semibold mb-4">{t('plans.whyExtended')}</h3>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center flex-shrink-0">
                <Wrench className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium mb-1">{t('plans.peaceOfMind')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('plans.peaceOfMindDesc')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-50 dark:bg-green-950/40 flex items-center justify-center flex-shrink-0">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium mb-1">{t('plans.saveMoney')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('plans.saveMoneyDesc')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-violet-50 dark:bg-violet-950/40 flex items-center justify-center flex-shrink-0">
                <Phone className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <p className="font-medium mb-1">{t('plans.expertSupport')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('plans.expertSupportDesc')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Plan Card ─────────────────────────────────────────────────────────────────

interface PlanCardProps {
  plan: ProtectionPlan;
  selected: boolean;
  onSelect: () => void;
}

function PlanCard({ plan, selected, onSelect }: PlanCardProps) {
  const { t } = useTranslation('upgrade');
  const tierColors: Record<string, string> = {
    basic: "from-blue-50 to-blue-100 dark:from-blue-950/20 dark:to-blue-900/20 border-blue-200 dark:border-blue-800",
    standard: "from-violet-50 to-violet-100 dark:from-violet-950/20 dark:to-violet-900/20 border-violet-200 dark:border-violet-800",
    premium: "from-amber-50 to-amber-100 dark:from-amber-950/20 dark:to-amber-900/20 border-amber-200 dark:border-amber-800",
  };

  const tierIcons: Record<string, string> = {
    basic: "bg-blue-600",
    standard: "bg-violet-600",
    premium: "bg-amber-600",
  };

  return (
    <button
      onClick={onSelect}
      className={cn(
        "relative rounded-2xl p-6 text-left transition-all",
        selected
          ? "border-4 border-foreground shadow-xl scale-105"
          : "border-2 hover:border-foreground/50 hover:scale-[1.02] hover:shadow-md",
        "bg-gradient-to-br",
        tierColors[plan.tier]
      )}
    >
      {/* Shield icon */}
      <div className={cn("absolute top-4 right-4 w-12 h-12 rounded-full flex items-center justify-center", tierIcons[plan.tier])}>
        <Shield className="h-6 w-6 text-white" />
      </div>

      {/* Tag */}
      {plan.tag && (
        <Badge className="absolute -top-3 left-4 whitespace-nowrap shadow-md">
          {plan.tag}
        </Badge>
      )}

      {/* Selected indicator */}
      {selected && (
        <div className="absolute -top-3 -right-3 h-10 w-10 rounded-full bg-green-600 flex items-center justify-center shadow-lg border-4 border-background">
          <CheckCircle2 className="h-5 w-5 text-white" />
        </div>
      )}

      <div className="mb-4 pr-14">
        <h3 className="text-lg font-bold mb-1">{plan.name}</h3>
        <p className="text-xs text-muted-foreground">{plan.provider}</p>
      </div>

      <div className="mb-4">
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold">{formatCurrency(plan.price)}</span>
        </div>
        <p className="text-xs text-muted-foreground">
          {t('plans.monthlyShort', { amount: formatCurrency(plan.monthlyPrice), duration: plan.duration, unit: plan.duration === 1 ? t('plans.year') : t('plans.years') })}
        </p>
      </div>

      {/* Deductible */}
      <div className="mb-4 p-3 rounded-lg bg-background/50 backdrop-blur-sm">
        <p className="text-xs font-semibold mb-0.5">{t('plans.deductible')}</p>
        <p className="text-sm font-bold">
          {plan.deductible === 0 ? t('plans.noDeductible') : formatCurrency(plan.deductible)}
        </p>
      </div>

      {/* Coverage */}
      <div className="space-y-2 mb-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('plans.coverage')}
        </p>
        <ul className="space-y-1.5">
          {plan.coverage.slice(0, 3).map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-xs">
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        {plan.coverage.length > 3 && (
          <p className="text-xs text-muted-foreground">
            {t('plans.moreBenefits', { count: plan.coverage.length - 3 })}
          </p>
        )}
      </div>

      <div className="pt-4 border-t">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5" />
          <span>{t('plans.viewTerms')}</span>
        </div>
      </div>
    </button>
  );
}
